import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { useError } from '../contexts/ErrorContext';
import { usePatientTabs } from './PatientTabs/PatientTabsContext';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  Table,
  Heading,
  Badge,
  Dialog,
  TextField,
  Callout,
  Tooltip,
  Select,
  Checkbox
} from '@radix-ui/themes';
import {
  ReloadIcon,
  MagnifyingGlassIcon,
  PersonIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DownloadIcon
} from '@radix-ui/react-icons';
import ScheduleScanDialog from './ScheduleScanDialog';
import CalendarImportDialog from './CalendarImportDialog';
import SmsUndeliverableBanner from './SmsUndeliverableBanner';
import { format, parseISO } from 'date-fns';
import { patientApi, billingApi, organizationApi } from '../services/api';
import { toast } from 'react-hot-toast';
import {
  classifyBloodPressure,
  getBPColor,
  getLatestBloodPressure
} from '../utils/bloodPressureValidation';
// PHA-1083: getBPSortPriority and matchesPatientSearch are no longer imported
// here — the BP sort and the name/MRN/DOB search both run in SQL now. Both
// functions remain the specification the backend is tested against (see
// test_pha1083_pagination.py) and are still used elsewhere, so neither was
// deleted.
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useSessionState } from '../hooks/useSessionState';
// PHA-1168: clinic-designed views. Phanova Standard is the table below, unchanged.
import { useCustomViews, useViewChoice, STANDARD } from './customViews/useCustomViews';
import ViewSelector from './customViews/ViewSelector';
import CustomPatientListView, { CustomPatientListViewHandle } from './customViews/CustomPatientListView';
import { customViewGridFilters } from './customViews/format';
import { collapsedPillRect, FLOATING_CHAT_CLEARANCE } from './chat/floatingChatGeometry';

type SortField = 'lastName' | 'firstName' | 'timeThisMonth' | 'latestAnswer' | 'lastSent' | 'startDate' | 'reviewDate' | 'nextDueDate' | 'bloodPressure' | 'bpDate' | 'hospitalization' | 'apcmTier' | 'rpmDataDays';
type SortDirection = 'asc' | 'desc';

interface NextDueInfo {
  date: string;
  type: 'task' | 'goal';
  name: string;
}

interface Patient {
  patient_id: string;
  first_name: string;
  last_name: string;
  date_of_birth: string;
  phone_number?: string;
  uploaded_by?: string;
  created_date?: string;
  last_successful_data_collection?: string | null;
  last_attempt_to_collect_data?: string | null;
  successful_attempts?: number | null;
  failed_attempts?: number | null;
  most_recent_surveys?: Array<{
    survey_id: string;
    name: string;
    created_at?: string;
    answers: any[];
  }>;
  answers?: any[];
  timeSpentThisMonth?: number;
  // Fields for onboarding status check
  problems?: any[];
  medications?: any[];
  care_plan_definitions?: any[];
  has_active_care_plan?: boolean;
  has_draft_care_plan?: boolean;
  has_active_tasks?: boolean;
  diseases?: string[];
  nextDueDate?: string | null;
  nextDueType?: 'task' | 'goal';
  nextDueName?: string;
  // Organization/clinic info
  organization_name?: string;
  last_hospitalization?: string | null;
  care_transition_events?: Array<{
    id: number;
    encounter_type: string;
    reported_date: string;
    reason_text?: string;
    created_at: string;
  }> | null;
  apcm_tier?: number | null;
  apcm_default_code?: string | null;
  consent_status?: 'collected' | 'denied' | null;
  ccm_start_date?: string | null;
  // PHA-1080: the active care plan's expiration_date -- the due date for creating
  // the NEXT plan, shown on the chart as "Next Review Date". Null for patients who
  // still need onboarding, because they have no active plan to have a due date.
  care_plan_review_date?: string | null;
  has_rpm_device?: boolean;
  has_rpm_data?: boolean;
  rpm_data_days?: number;
  rpm_cycle_start?: string | null;
  rpm_cycle_end?: string | null;
  has_appointment_today?: boolean;
  // PHA-1083 follow-up: the latest blood pressure, resolved server-side. Present
  // only on the lean payload; when absent the cell falls back to parsing the raw
  // answers, exactly as before.
  latest_bp_systolic?: number | null;
  latest_bp_diastolic?: number | null;
  latest_bp_date?: string | null;
  [key: string]: any;
}

/**
 * PHA-1083 follow-up: the grid's BP and BP Date cells.
 *
 * The server now resolves the latest reading — the same one the BP sort ranks
 * on — so the browser no longer receives every survey answer just to derive it.
 * `getLatestBloodPressure` stays as the fallback for a response that still
 * carries the raw answers, which keeps this correct against a backend that
 * predates the lean payload.
 */
const latestBloodPressureFor = (patient: Patient) => {
  if (patient.latest_bp_systolic != null || patient.latest_bp_diastolic != null) {
    return {
      systolic: patient.latest_bp_systolic ?? undefined,
      diastolic: patient.latest_bp_diastolic ?? undefined,
      date: patient.latest_bp_date ?? undefined,
    };
  }
  return getLatestBloodPressure(patient);
};

// PHA-1083: the grid opens on "Onboarded". Named because the empty state has to
// distinguish "the default view is empty" from "your filters exclude everyone".
const DEFAULT_STATUS_FILTER = 'onboarded';

// PHA-1159: how many times to silently re-probe the API after a transient auth failure
// before giving up and asking the user to log in by hand. With exponential backoff
// (1s, 2s, 4s, 8s, 15s) this rides out an Auth0 rate-limit window — which is capped at
// AUTH0_RATE_LIMIT_BACKOFF_MAX_SECONDS (60s) server-side — without any user action.
const AUTH_RECOVERY_MAX_ATTEMPTS = 5;

// Helper function to detect if patient needs to continue onboarding (same logic as PatientDetail)
const needsContinueOnboarding = (patient: Patient): boolean => {
  const hasCarePlans = patient.has_active_care_plan
    ?? (Array.isArray(patient.care_plan_definitions) && patient.care_plan_definitions.length > 0);
  return !hasCarePlans;
};


const PatientList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { orgId: currentOrgId } = useOrganization();
  const { showError } = useError();
  // PHA-509: Patient detail now lives in a Dashboard tab, not a modal.
  // PatientList delegates open/focus to the shared PatientTabs context.
  // PHA-537: also read activeTabId so we can background-refresh when the
  // patients tab regains focus (in-app tab switch or browser tab return).
  const { openOrFocus, activeTabId } = usePatientTabs();
  // PHA-1168: the saved view this tab shows. null = Phanova Standard.
  const customViews = useCustomViews(currentOrgId, 'patient_list');
  const { setChoice: setViewChoice, activeViewId } = useViewChoice(
    currentOrgId, 'patient_list', customViews.views, customViews.enabled,
  );
  const activeView = customViews.views.find((v) => v.id === activeViewId) || null;
  const customViewRef = useRef<CustomPatientListViewHandle>(null);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  // PHA-537: separate refresh state so the background refetch keeps the
  // existing list visible — only the initial mount uses the blocking spinner.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const initialLoadCompleteRef = useRef(false);
  const lastActiveTabIdRef = useRef<string>(activeTabId);
  // PHA-1187: bumped on every background reload so the SMS-undeliverable banner re-fetches
  // too — a number fixed in a patient tab must drop off the banner on return to the list.
  const [smsBannerRefreshKey, setSmsBannerRefreshKey] = useState(0);
  const backgroundAbortRef = useRef<AbortController | null>(null);
  const loadPatientsRef = useRef<
    ((
      signal?: AbortSignal,
      options?: { background?: boolean; force?: boolean; bypassAuthGate?: boolean },
    ) => Promise<void>) | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [authenticationInProgress, setAuthenticationInProgress] = useState(false);
  const [showLoginButton, setShowLoginButton] = useState(false);
  // PHA-1159: a transient backend auth failure used to latch this screen permanently.
  // `authRecoveryTick` counts background re-probes while we're in that state; when a
  // probe succeeds the whole banner clears, and only once the attempts are exhausted
  // do we fall back to asking the user to log in by hand.
  const [authRecoveryTick, setAuthRecoveryTick] = useState(0);
  // PHA-1159: what kind of failure we last saw. 'transient' (a 503 — the backend asked
  // us to retry) must never graduate into a "please login" prompt no matter how many
  // times it repeats; only a real 'auth' failure may.
  const lastAuthFailureKindRef = useRef<'auth' | 'transient' | null>(null);
  const [scanDialogOpen, setScanDialogOpen] = useState(false);
  const [calendarDialogOpen, setCalendarDialogOpen] = useState(false);
  const [calendarFilterEnabled, setCalendarFilterEnabled] = useSessionState<boolean>('patientList.calendarFilterEnabled', false);
  const [filteredPatientIds, setFilteredPatientIds] = useState<string[]>([]);
  const [deletingPatients, setDeletingPatients] = useState<Set<string>>(new Set());
  const [billingData, setBillingData] = useState<Record<string, number>>({});
  const [sortField, setSortField] = useSessionState<SortField>('patientList.sortField', 'lastName');
  const [sortDirection, setSortDirection] = useSessionState<SortDirection>('patientList.sortDirection', 'asc');
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [statusFilter, setStatusFilter] = useSessionState<'all' | 'needsOnboarding' | 'onboarded'>('patientList.statusFilter', DEFAULT_STATUS_FILTER);
  const [onboardingSubFilter, setOnboardingSubFilter] = useSessionState<'all' | 'notCollected' | 'denied'>('patientList.onboardingSubFilter', 'all');
  // PHA-890: onboarding stage sub-filter (independent of consent). chartReview = no
  // drafted plan yet (raw chart upload); carePlanReview = drafted plan awaiting review.
  const [onboardingStageFilter, setOnboardingStageFilter] = useSessionState<'all' | 'chartReview' | 'carePlanReview'>('patientList.onboardingStageFilter', 'all');
  const [apcmTierFilter, setApcmTierFilter] = useSessionState<'all' | 'tier1' | 'tier2'>('patientList.apcmTierFilter', 'all');
  const [appointmentTodayFilter, setAppointmentTodayFilter] = useSessionState<boolean>('patientList.appointmentTodayFilter', false);

  // PHA-1083: the grid is paged, searched, filtered and sorted server-side.
  // `page` is 1-based. `totalCount` is the size of the whole filtered set, which
  // the backend returns alongside the page — that is the point of the change:
  // the badge can say "of 137" without the browser ever holding 137 fat rows.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useSessionState<number>('patientList.pageSize', 50);
  const [totalCount, setTotalCount] = useState(0);
  // Org switches keep the blocking spinner (showing another clinic's patients
  // while the new ones load is worse than a spinner); paging, searching and
  // sorting use the subtle refresh indicator instead.
  const lastLoadedOrgRef = useRef<string | undefined>(undefined);

  // PHA-723: programs this clinic supports; drives which program-specific columns
  // show. Defaults to all three (nothing hidden) until resolved / on error.
  const [enabledPrograms, setEnabledPrograms] = useState<Set<string>>(new Set(['APCM', 'CCM', 'RPM']));

  const showClinicColumn = currentOrgId === 'ALL';
  // PHA-723: the APCM tier column + filter follow the clinic's program config
  // ('ALL' internal view always shows them). Per the ticket, the patient list
  // only gates APCM — RPM/CCM columns are unchanged.
  const showApcmColumns = currentOrgId === 'ALL' || enabledPrograms.has('APCM');
  // PHA-898: the APCM tier filter is only meaningful where a tier is actually
  // assigned — the Onboarded view, and the Care Plan Review stage of Needs
  // Onboarding. Everywhere else it would filter on a tier the patient does not
  // have yet, so it stays hidden (and always renders as the right-most filter).
  const showApcmTierFilter = showApcmColumns && (
    statusFilter === 'onboarded' ||
    (statusFilter === 'needsOnboarding' && onboardingStageFilter === 'carePlanReview')
  );

  // Sticky header: set --table-viewport-height on the Card so the CSS can
  // constrain the ScrollArea viewport, making it the scroll container for
  // position:sticky on <thead>. See index.css ".patient-list-table" rules.
  const tableCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const card = tableCardRef.current;
    if (!card) return;

    const updateHeight = () => {
      const rect = card.getBoundingClientRect();
      // End the table above the closed "Ask Phanova AI" pill when the pill
      // sits over it: clinics without the Smart Hub rail, and narrow screens
      // where the rail stacks below the list.
      const pill = collapsedPillRect(window.innerWidth, window.innerHeight);
      const bottom = pill.left < rect.right && pill.right > rect.left
        ? window.innerHeight - FLOATING_CHAT_CLEARANCE
        : window.innerHeight;
      const available = bottom - rect.top;
      if (available > 200) {
        card.style.setProperty('--table-viewport-height', `${available}px`);
      }
    };

    updateHeight();

    // Defer to the next frame so the CSS-var write can't synchronously
    // re-trigger this observer ("ResizeObserver loop completed with undelivered
    // notifications" — benign, but CRA's dev overlay surfaces it as an error).
    let raf = 0;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateHeight);
    });
    observer.observe(document.body);
    // The card narrows without the body resizing when the Smart Hub rail
    // appears (its hide flag answers after mount).
    observer.observe(card);
    window.addEventListener('resize', updateHeight, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [loading]);

  // Next due date requires care_plan_definitions with tasks/goals detail,
  // which is only available from the on-demand detail endpoint.
  // Returns null for the lightweight grid response.
  const computeNextDueDate = (patient: Patient): NextDueInfo | null => {
    if (!patient.care_plan_definitions || patient.care_plan_definitions.length === 0) {
      return null;
    }

    const dueItems: Array<{ date: string; type: 'task' | 'goal'; name: string }> = [];

    patient.care_plan_definitions.forEach((carePlan: any) => {
      if (carePlan.tasks) {
        carePlan.tasks.forEach((task: any) => {
          if (task.due_date && task.completed !== true) {
            dueItems.push({
              date: task.due_date,
              type: 'task',
              name: task.task || task.completion_criteria || 'Unnamed task'
            });
          }
        });
      }

      if (carePlan.goals) {
        carePlan.goals.forEach((goal: any) => {
          if (goal.due_date && goal.achieved !== true) {
            dueItems.push({
              date: goal.due_date,
              type: 'goal',
              name: goal.measure || 'Unnamed goal'
            });
          }
        });
      }
    });

    if (dueItems.length === 0) return null;

    dueItems.sort((a, b) => a.date.localeCompare(b.date));
    return dueItems[0];
  };

  // SortableHeader component
  const SortableHeader: React.FC<{ field: SortField; label: string; style?: React.CSSProperties; tooltip?: React.ReactNode }> = ({ field, label, style, tooltip }) => {
    const isActive = sortField === field;

    const handleClick = () => {
      if (isActive) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    };

    const headerContent = (
      <Flex align="center" gap="1">
        <Text>{label}</Text>
        {isActive ? (
          <Text
            size="2"
            weight="bold"
            style={{ color: '#1e293b', lineHeight: 1 }}
          >
            {sortDirection === 'asc' ? '▲' : '▼'}
          </Text>
        ) : (
          <Flex direction="column" align="center" style={{ opacity: 0.5, fontSize: '8px', lineHeight: 1.1 }}>
            <span>▲</span>
            <span>▼</span>
          </Flex>
        )}
      </Flex>
    );

    return (
      <Table.ColumnHeaderCell
        onClick={handleClick}
        style={{ cursor: 'pointer', userSelect: 'none', verticalAlign: 'middle', overflow: 'hidden', ...style }}
        className="hover:bg-slate-100"
      >
        {tooltip ? (
          <Tooltip content={tooltip} delayDuration={200}>
            <span>{headerContent}</span>
          </Tooltip>
        ) : (
          headerContent
        )}
      </Table.ColumnHeaderCell>
    );
  };

  // PHA-1083: the filter set the backend applies. Every dimension the grid
  // offers is resolved server-side now — applying these to just the rows that
  // happen to be on the current page would quietly give wrong answers (a "Tier
  // 2" filter would show only the Tier 2 patients within page 1).
  //
  // The nesting mirrors the old client-side predicate exactly: the onboarding
  // stage and consent sub-filters only apply inside "Needs Onboarding".
  const gridFilters = useMemo(() => {
    const filters: Record<string, any> = {};
    if (statusFilter !== 'all') filters.status = statusFilter;
    if (statusFilter === 'needsOnboarding') {
      if (onboardingStageFilter !== 'all') filters.onboarding_stage = onboardingStageFilter;
      if (onboardingSubFilter !== 'all') filters.consent = onboardingSubFilter;
    }
    if (apcmTierFilter === 'tier1') filters.apcm_tier = 1;
    else if (apcmTierFilter === 'tier2') filters.apcm_tier = 2;
    if (appointmentTodayFilter) filters.appointment_today = true;
    // The calendar dialog resolves a set of ids; passing them through keeps the
    // filter correct across pages.
    if (calendarFilterEnabled && filteredPatientIds.length > 0) {
      filters.patient_ids = filteredPatientIds;
    }
    return filters;
  }, [
    statusFilter, onboardingStageFilter, onboardingSubFilter, apcmTierFilter,
    appointmentTodayFilter, calendarFilterEnabled, filteredPatientIds,
  ]);

  // PHA-1168: only search, Appointment Today and the calendar import narrow a
  // custom view; the status/stage/consent/tier selects are hidden while one is on.
  const viewGridFilters = useMemo(() => customViewGridFilters(gridFilters), [gridFilters]);

  // PHA-1083: changing a filter while on page 4 would otherwise fire two loads
  // in the same commit — one at the stale offset, aborted, then one at page 1.
  // The aborted request still reaches the database, and a high OFFSET is the
  // expensive end of the query, so resolve the page here instead: any change to
  // *what* is being asked for is page 1 immediately, not one render later.
  const requestKey = JSON.stringify([
    currentOrgId, debouncedSearchQuery, sortField, sortDirection, gridFilters, pageSize,
  ]);
  const requestKeyRef = useRef(requestKey);
  const effectivePage = requestKeyRef.current === requestKey ? page : 1;

  // PHA-1083 follow-up: after a page loads, quietly fetch the NEXT one. Paging
  // forward is the overwhelmingly common move, and this turns the click from a
  // round-trip into a state update. One slot, not a cache: it is discarded the
  // moment the search, sort, filters or org change, so it can never serve rows
  // that no longer match what is on screen.
  const prefetchRef = useRef<{ key: string; page: number; data: any } | null>(null);
  const prefetchAbortRef = useRef<AbortController | null>(null);

  // Applying a response is shared by the live fetch and the prefetch slot, so
  // the two can never diverge in how they shape a row.
  const applyPatientsResponse = useCallback((data: any) => {
    const patientsWithDetails = (data.records || data).map((patient: Patient) => ({
      ...patient,
      care_plan_definitions: patient.care_plan_definitions || [],
      diseases: patient.diseases || [],
    }));
    setPatients(patientsWithDetails);
    // PHA-1083: the size of the whole filtered set, not of this page. Falling
    // back to the page length keeps the badge sane against an older backend
    // that doesn't return the field yet.
    setTotalCount(
      typeof data.total_count === 'number' ? data.total_count : patientsWithDetails.length
    );
    lastLoadedOrgRef.current = currentOrgId;
    setError(null);
  }, [currentOrgId]);

  const gridRequestOptions = useCallback((targetPage: number) => ({
    page: targetPage,
    pageSize,
    search: debouncedSearchQuery,
    sortField,
    sortDirection,
    filters: gridFilters,
    // PHA-1083 follow-up: ~45% smaller payload. The raw answers only ever fed
    // the BP cell, which now arrives resolved.
    lean: true,
  }), [pageSize, debouncedSearchQuery, sortField, sortDirection, gridFilters]);

  // Best-effort: a failed or aborted prefetch just means the next click pays for
  // itself, which is the behavior without this at all.
  const schedulePrefetch = useCallback((targetPage: number, key: string, previous: any) => {
    if (!currentOrgId || targetPage < 2) return;
    if (previous && previous.has_more === false) return;
    prefetchAbortRef.current?.abort();
    const controller = new AbortController();
    prefetchAbortRef.current = controller;
    (async () => {
      try {
        const data = await patientApi.listWithDetails(
          controller.signal, currentOrgId, gridRequestOptions(targetPage),
        );
        prefetchRef.current = { key, page: targetPage, data };
      } catch {
        /* no-op: the click will fetch normally */
      }
    })();
  }, [currentOrgId, gridRequestOptions]);

    const loadPatients = useCallback(async (
    signal?: AbortSignal,
    options?: { background?: boolean; force?: boolean; bypassAuthGate?: boolean },
  ) => {
    // PHA-537: when background=true, do not flip the full-screen `loading`
    // spinner — keep the current list visible and use `isRefreshing` for a
    // subtle indicator. The initial mount and org switch still use the
    // blocking spinner because there's no existing data to show.
    const isBackground = options?.background === true;
    // PHA-1159: the recovery probe below must be able to run *while*
    // `authenticationInProgress` is set — that flag is precisely the state it exists to
    // get us out of. Everything else still respects the gate.
    if (!user || (authenticationInProgress && !options?.bypassAuthGate)) {
      console.log('❌ No user authentication or auth in progress');
      return;
    }

    // PHA-1083 follow-up: serve the page we already fetched, if it is the one
    // being asked for. `force` bypasses this — the PHA-537 focus refresh exists
    // precisely to get fresh rows, so it must never be answered from a slot
    // that was filled before the user left the tab.
    const prefetched = prefetchRef.current;
    if (
      !options?.force &&
      prefetched &&
      prefetched.key === requestKey &&
      prefetched.page === effectivePage
    ) {
      prefetchRef.current = null;
      applyPatientsResponse(prefetched.data);
      initialLoadCompleteRef.current = true;
      // Both flags, not just `loading`. The live path's `finally` deliberately
      // skips the reset when its request was aborted, so if `isRefreshing` is
      // already true and that load is cancelled, only a *live* load ever clears
      // it. Serving from the prefetch slot without clearing it here latches the
      // flag on — and `isRefreshing` disables Prev/Next, so paging would stop
      // working entirely. Rapid paging is exactly what this change invites.
      setLoading(false);
      setIsRefreshing(false);
      schedulePrefetch(effectivePage + 1, requestKey, prefetched.data);
      return;
    }
    if (options?.force) {
      prefetchAbortRef.current?.abort();
      prefetchRef.current = null;
    }

    if (isBackground) setIsRefreshing(true);
    else setLoading(true);
    try {
      console.log('🔍 Loading patients from API...');
      console.log('🔑 User authenticated:', user ? 'Yes' : 'No');

      // PHA-527: explicitly plumb the active org so the backend stops relying
      // on the implicit "internal user → ALL" fallback. When the user picks
      // "All Organizations" we send "ALL"; otherwise we send the specific id.
      // Skip the call entirely if the user has no selection yet (avoids a
      // confusing 403 / NO_ORGANIZATION_ASSIGNED for multi-clinic non-internal
      // users who haven't picked a clinic — the dropdown surfaces "Select
      // organization" in that state, see PHA-503).
      if (!currentOrgId) {
        if (isBackground) setIsRefreshing(false);
        else setLoading(false);
        return;
      }
      const data = await patientApi.listWithDetails(
        signal, currentOrgId, gridRequestOptions(effectivePage),
      );

      console.log('✅ Patients loaded:', data);

      // PHA-1083: the monthly billing report is per-ORG and per-month — it does
      // not vary by page, search or sort. It used to be fetched inside this
      // function, which was fine when this ran once per org load; now that
      // paging and every keystroke land here it would re-pull the whole org's
      // billing report each time. It has moved to its own effect below, and the
      // Time (min) column reads the resulting map when the rows are enriched.
      applyPatientsResponse(data);

      // PHA-1159: the call went through, so whatever made auth fail is over. Clear the
      // latched banner instead of leaving the user staring at a login prompt they no
      // longer need to act on.
      lastAuthFailureKindRef.current = null;
      if (authenticationInProgress || showLoginButton) {
        console.log('✅ Auth recovered - clearing login banner');
        setAuthenticationInProgress(false);
        setShowLoginButton(false);
        setAuthRecoveryTick(0);
        setError(null);
      }

      // PHA-1083 follow-up: warm the next page while the user reads this one.
      schedulePrefetch(effectivePage + 1, requestKey, data);
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      console.error('❌ Failed to load patients:', error);

      // PHA-810: 503 = transient backend unavailability (auth couldn't reach the DB).
      // apiRequest already retried; this is NOT an auth failure, so never show a hard
      // error or trigger the login flow — keep current data and let the next poll
      // recover. This is the path that previously force-logged users out on a DB blip.
      const is503 = error?.errorData?.status === 503 || (error instanceof Error && error.message.includes('503'));
      if (is503) {
        console.warn('⚠️ Transient 503 loading patients - keeping current data, will recover on next load');
        // PHA-1159: record that the *last* failure was retryable. The recovery ladder
        // below reads this: a run of 503s must never end in "please login", because the
        // backend is explicitly telling us to retry. Without this, the Auth0 rate limit
        // now surfaces as 503 and would still have marched the user to a login prompt.
        lastAuthFailureKindRef.current = 'transient';
        if (!isBackground || options?.bypassAuthGate) setError('Reconnecting…');
        return;
      }

      // Show structured error modal if available
      if (error.errorData) {
        showError(error.errorData);
        setError('Failed to load patients. See error details above.');
        return;
      }

      if (error instanceof Error) {
        if (error.message.includes('Authentication required') || error.message.includes('please login manually')) {
          // PHA-1159: do NOT latch the login banner on the first failure. On 2026-09-22
          // Auth0 rate-limited us for ~80 seconds; the backend answered 401 and this
          // branch parked every user on a manual Login prompt that stayed up long after
          // the backend recovered. Enter recovery mode instead — the effect below
          // re-probes with backoff, and the Login button only appears if those all fail.
          console.log('🔒 Authentication required - entering background recovery');
          lastAuthFailureKindRef.current = 'auth';
          setError('Reconnecting…');
          setAuthenticationInProgress(true);
        } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
          setError('Authentication failed. Please login again.');
          setTimeout(() => {
            const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5802';
            window.location.href = `${baseUrl}/login`;
          }, 2000);
        } else if (error.message.includes('CORS') || error.message.includes('network')) {
          setError('Unable to connect to server. Using offline data.');
          console.log('📋 Using mock patient data due to connection issues');

          // Fallback to mock data
          setPatients([
            {
              patient_id: 'P001',
              first_name: 'Alice',
              last_name: 'Johnson',
              date_of_birth: '1985-03-15',
              uploaded_by: 'Dr. Sarah Johnson',
              created_date: '2024-03-10'
            },
            {
              patient_id: 'P002',
              first_name: 'Bob',
              last_name: 'Smith',
              date_of_birth: '1978-07-22',
              uploaded_by: 'Dr. Sarah Johnson',
              created_date: '2024-03-09'
            },
            {
              patient_id: 'P003',
              first_name: 'Carol',
              last_name: 'Williams',
              date_of_birth: '1992-11-08',
              uploaded_by: 'Dr. Sarah Johnson',
              created_date: '2024-03-08'
            }
          ]);
        } else {
          setError(`Error loading patients: ${error.message}`);
        }
      } else {
        setError('Unknown error occurred while loading patients');
      }
    } finally {
      if (!signal?.aborted) {
        if (isBackground) setIsRefreshing(false);
        else setLoading(false);
        // PHA-537: gate the focus-driven background refetch on at least one
        // completed (non-aborted) load. Set after either flavor finishes.
        initialLoadCompleteRef.current = true;
      }
    }
  }, [
    user, authenticationInProgress, currentOrgId,
    // PHA-1083: paging, search, sort and filters are server-side, so each is a
    // reason to refetch. The mount effect below aborts the in-flight request
    // before starting the next one, so typing does not queue a chain of them.
    effectivePage, pageSize, debouncedSearchQuery, sortField, sortDirection, gridFilters,
    requestKey, applyPatientsResponse, gridRequestOptions, schedulePrefetch,
  ]);

  // PHA-1083: the current month's billable minutes for the whole org, fetched
  // once per org rather than once per page. Keyed by patient_id and merged into
  // the rows below; patients outside the current page are simply never looked up.
  useEffect(() => {
    if (!user || authenticationInProgress || !currentOrgId) return;
    const controller = new AbortController();
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const fromDate = `${month}-01`;
    const toDate = `${month}-${String(now.getDate()).padStart(2, '0')}`;

    (async () => {
      try {
        const billingResponse = await billingApi.getMonthlyReport(
          null, fromDate, toDate, currentOrgId, controller.signal,
        );
        const timeMap: Record<string, number> = {};
        if (billingResponse.records && Array.isArray(billingResponse.records)) {
          billingResponse.records.forEach((record: any) => {
            if (record.Month === month && record.patient_id) {
              timeMap[record.patient_id] = record['spent (minutes)'] || 0;
            }
          });
        }
        setBillingData(timeMap);
      } catch (billingError: any) {
        if (billingError?.name === 'AbortError') return;
        // Non-fatal: the Time column falls back to 0. It never blocked the list
        // before and must not start blocking it now.
        console.error('⚠️ Failed to load billing data:', billingError);
        setBillingData({});
      }
    })();

    return () => { controller.abort(); };
  }, [user, authenticationInProgress, currentOrgId]);

  // PHA-1083: any change to *what* is being asked for restarts at page 1 —
  // staying on page 4 while narrowing a filter lands the user on an empty grid
  // with no indication why. `effectivePage` has already treated this render as
  // page 1, so the fetch never goes out at the stale offset; this only brings
  // the state in line for the controls.
  useEffect(() => {
    if (requestKeyRef.current !== requestKey) {
      requestKeyRef.current = requestKey;
      setPage(1);
    }
  }, [requestKey]);

  useEffect(() => {
    if (user && !authenticationInProgress) {
      const abortController = new AbortController();
      // PHA-1083: this effect now also fires for paging, search, sort and filter
      // changes. Those keep the current rows on screen with the subtle refresh
      // indicator; only the first load and an org switch get the blocking
      // spinner, because showing another clinic's patients while the new ones
      // load is worse than showing nothing.
      const isOrgSwitch = lastLoadedOrgRef.current !== currentOrgId;
      const background = initialLoadCompleteRef.current && !isOrgSwitch;
      loadPatients(abortController.signal, { background });
      return () => { abortController.abort(); };
    } else if (user && authenticationInProgress) {
      // PHA-1159: we HAVE a signed-in user — this is a backend auth failure being
      // retried in the background, not a missing login. Say so honestly, and don't
      // claim they need to log in until the recovery probes have actually given up.
      setError(
        authRecoveryTick >= AUTH_RECOVERY_MAX_ATTEMPTS
          && lastAuthFailureKindRef.current !== 'transient'
          ? 'Authentication required. Please login.'
          : 'Reconnecting…',
      );
      setLoading(false);
    } else {
      console.log('⏳ Waiting for user authentication...');
      setError('Authentication required. Please login.');
      setLoading(false);

      // If no user authentication after 3 seconds, redirect to login
      const timeoutId = setTimeout(() => {
        if (!user && !authenticationInProgress) {
          console.log('🔄 No authentication detected, redirecting to login...');
          const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5802';
          window.location.href = `${baseUrl}/login`;
        }
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [user, authenticationInProgress, authRecoveryTick, loadPatients]);

  // PHA-1159: background recovery from a transient auth failure. While we hold a user
  // but the backend rejected us, re-probe with exponential backoff. A success clears the
  // banner (see loadPatients). Only when every attempt has failed do we surface the
  // manual Login button — the behavior this screen used to jump to immediately.
  useEffect(() => {
    if (!user || !authenticationInProgress) return;

    const attemptsExhausted = authRecoveryTick >= AUTH_RECOVERY_MAX_ATTEMPTS;
    // A run of 503s means the backend is up and explicitly asking us to retry — keep
    // probing at the capped interval forever rather than telling the user to log in,
    // which is what PHA-810 already does for a foreground 503.
    const stillTransient = lastAuthFailureKindRef.current === 'transient';

    if (attemptsExhausted && !stillTransient) {
      console.log('🔒 Auth recovery exhausted - surfacing manual login');
      setShowLoginButton(true);
      return;
    }

    const delayMs = attemptsExhausted
      ? 15000
      : Math.min(15000, 1000 * 2 ** authRecoveryTick);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log(`🔄 Auth recovery probe ${authRecoveryTick + 1}/${AUTH_RECOVERY_MAX_ATTEMPTS}`);
      const probe = loadPatientsRef.current?.(
        controller.signal, { background: true, bypassAuthGate: true },
      );
      // Advance the attempt counter either way — a probe that never ran must not stall
      // the ladder, or the banner would hang on "Reconnecting…" forever.
      if (probe) probe.finally(() => setAuthRecoveryTick((t) => t + 1));
      else setAuthRecoveryTick((t) => t + 1);
    }, delayMs);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [user, authenticationInProgress, authRecoveryTick]);

  // PHA-537: keep `loadPatientsRef` pointing at the latest `loadPatients`
  // closure so the focus-effect below can call it without re-subscribing
  // every time `currentOrgId` / `user` change. That decouples the focus
  // refetch from the mount effect — org switches stay full-screen via the
  // mount effect, in-app tab focus stays background via the focus effect.
  useEffect(() => {
    loadPatientsRef.current = loadPatients;
  }, [loadPatients]);

  // PHA-537: refresh the patient list when its dashboard tab regains focus.
  // Two triggers, both background (no full-screen spinner):
  //   1. In-app tab switch: activeTabId transitions from another tab back
  //      to 'patients' (covers the post-onboarding return that motivated
  //      this issue — onboarding's resetWorkflow() navigates back here).
  //   2. Browser tab return: visibilitychange fires while we're already on
  //      the patients tab.
  // Initial mount is handled by the existing useEffect above; we only fire
  // here after `initialLoadCompleteRef` flips to true so we never race the
  // first load.
  useEffect(() => {
    const triggerBackgroundReload = () => {
      if (!initialLoadCompleteRef.current) return;
      // Cancel any in-flight background refetch from an earlier focus event
      // — rapid tab toggling shouldn't queue a chain of pending requests.
      backgroundAbortRef.current?.abort();
      const controller = new AbortController();
      backgroundAbortRef.current = controller;
      // force: this refresh exists to pick up changes made elsewhere, so it must
      // go to the network rather than be answered from the prefetch slot.
      loadPatientsRef.current?.(controller.signal, { background: true, force: true });
      setSmsBannerRefreshKey((k) => k + 1);
    };

    if (activeTabId === 'patients') {
      if (lastActiveTabIdRef.current !== 'patients') {
        triggerBackgroundReload();
      }

      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          triggerBackgroundReload();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      lastActiveTabIdRef.current = activeTabId;
      return () => {
        document.removeEventListener('visibilitychange', onVisibilityChange);
      };
    }

    lastActiveTabIdRef.current = activeTabId;
  }, [activeTabId]);

  useEffect(() => {
    if (!showApcmTierFilter) {
      setApcmTierFilter('all');
    }
  }, [showApcmTierFilter]);

  // PHA-723: resolve which programs the current clinic supports so we can hide
  // program-specific columns. 'ALL'/unset shows everything; errors degrade to
  // showing everything (the billing gate remains the source of truth).
  useEffect(() => {
    let cancelled = false;
    if (!currentOrgId || currentOrgId === 'ALL') {
      setEnabledPrograms(new Set(['APCM', 'CCM', 'RPM']));
      return;
    }
    organizationApi.getEffectiveSupportedPrograms(currentOrgId)
      .then((data: any) => {
        if (cancelled) return;
        const progs = Array.isArray(data?.programs) ? data.programs : ['APCM', 'CCM', 'RPM'];
        setEnabledPrograms(new Set(progs));
      })
      .catch(() => { if (!cancelled) setEnabledPrograms(new Set(['APCM', 'CCM', 'RPM'])); });
    return () => { cancelled = true; };
  }, [currentOrgId]);

  // PHA-509: row clicks open or focus a patient tab in Dashboard. Tab body
  // fetches fresh details on mount, so PatientList no longer needs to await
  // patientApi.get before showing anything. The row snapshot (first / last /
  // MRN) is passed for the tab label.
  const handleViewPatient = (patientId: string) => {
    const snapshot = patients.find((p) => p.patient_id === patientId);
    openOrFocus({
      patientId,
      firstName: snapshot?.first_name,
      lastName: snapshot?.last_name,
      mrn: snapshot?.mrn,
    });
  };

  const handleRemovePatient = async (patientId: string, patientName: string) => {
    if (!window.confirm(`Are you sure you want to remove ${patientName} from the patient list? This action cannot be undone.`)) {
      return;
    }

    setDeletingPatients(prev => new Set(Array.from(prev).concat(patientId)));
    
    try {
      await patientApi.delete(patientId);
      toast.success(`Patient ${patientName} has been removed successfully`);
      
      // Remove patient from local state
      setPatients(prev => prev.filter(p => p.patient_id !== patientId));
    } catch (error: any) {
      console.error('Failed to remove patient:', error);
      
      // Show structured error modal if available
      if (error.errorData) {
        showError(error.errorData);
      } else if (error instanceof Error) {
        toast.error(`Failed to remove patient: ${error.message}`);
      } else {
        toast.error('Failed to remove patient. Please try again.');
      }
    } finally {
      setDeletingPatients(prev => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(patientId);
        return newSet;
      });
    }
  };

  // Enrich patients with computed nextDueDate
  const patientsWithDueDate = useMemo(() => {
    return patients.map(patient => {
      const dueInfo = computeNextDueDate(patient);
      return {
        ...patient,
        // PHA-1083: merged here rather than inside loadPatients, so a page change
        // does not re-pull the whole org's billing report. The server sorts
        // "Time (min)" on the same billable-minutes rule this report uses.
        timeSpentThisMonth: billingData[patient.patient_id] || 0,
        nextDueDate: dueInfo?.date || null,
        nextDueType: dueInfo?.type,
        nextDueName: dueInfo?.name
      };
    });
  }, [patients, billingData]);

  // Filter and sort patients based on search query, filters, sort field and direction
  // PHA-1083: search, every filter dimension and the sort all run in SQL now, so
  // the rows arrive in the order they should be displayed and this is just a
  // rename. It used to filter and sort the entire org in memory on every
  // keystroke; the array here is one page.
  //
  // The sort is NOT re-applied client-side. Re-sorting a page would reorder
  // those 50 rows among themselves and silently disagree with the pagination,
  // which is exactly the bug this ticket exists to prevent.
  const sortedPatients = patientsWithDueDate;

  // PHA-1083: pagination arithmetic, derived from the backend's total.
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.min(page, totalPages);
  const rangeStart = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, totalCount);

  // A compact window of page numbers around the current one, so a clinic with 40
  // pages doesn't render 40 buttons.
  const pageNumbers = useMemo(() => {
    const span = 2;
    const numbers: number[] = [];
    for (let n = Math.max(1, currentPage - span); n <= Math.min(totalPages, currentPage + span); n += 1) {
      numbers.push(n);
    }
    return numbers;
  }, [currentPage, totalPages]);

  const [exporting, setExporting] = useState(false);

  // PHA-1083: has the user narrowed the list beyond the DEFAULT view? Not
  // "is gridFilters non-empty" — statusFilter defaults to 'onboarded', so that
  // is always true and a clinic with genuinely no patients would be told
  // "nothing matches your filters" instead of "add your first patient".
  const hasActiveNarrowing =
    Boolean(debouncedSearchQuery.trim()) ||
    statusFilter !== DEFAULT_STATUS_FILTER ||
    onboardingSubFilter !== 'all' ||
    onboardingStageFilter !== 'all' ||
    apcmTierFilter !== 'all' ||
    appointmentTodayFilter ||
    calendarFilterEnabled;

  // Resets to the DEFAULT view, not to "all" — clearing a search should not
  // silently switch which cohort the clinician is looking at.
  const handleClearFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter(DEFAULT_STATUS_FILTER);
    setOnboardingSubFilter('all');
    setOnboardingStageFilter('all');
    setApcmTierFilter('all');
    setAppointmentTodayFilter(false);
    setCalendarFilterEnabled(false);
    setFilteredPatientIds([]);
    setPage(1);
  }, [
    setSearchQuery, setStatusFilter, setOnboardingSubFilter, setOnboardingStageFilter,
    setApcmTierFilter, setAppointmentTodayFilter, setCalendarFilterEnabled,
  ]);

  const handleExportCSV = useCallback(async () => {
    // PHA-1083: the export covers the whole filtered set, NOT the page on
    // screen. Before pagination `sortedPatients` was everything, so exporting it
    // was right; afterwards it would silently have become "export these 50" —
    // and a clinician would not notice until after they had sent the file. So
    // re-fetch unpaged with the same search, sort and filters.
    setExporting(true);
    let patientsToExport: Patient[];
    try {
      const data = await patientApi.listWithDetails(undefined, currentOrgId, {
        search: debouncedSearchQuery,
        sortField,
        sortDirection,
        filters: gridFilters,
        // The CSV columns are all scalars, so it wants the lean payload too —
        // over the whole filtered set that is the difference between ~1 MB and
        // ~0.5 MB of JSON the browser has to parse before it can build a file.
        lean: true,
      });
      patientsToExport = (data.records || data) as Patient[];
    } catch (error: any) {
      console.error('❌ Failed to fetch the full list for export:', error);
      // Deliberately no partial-page fallback: a CSV that silently contains 50
      // of 137 patients is worse than no CSV, because nothing on the file says
      // it is incomplete.
      toast.error('Could not export the full list. Please try again.');
      setExporting(false);
      return;
    } finally {
      setExporting(false);
    }

    const headers = ['Last Name', 'First Name', 'Date of Birth', 'Phone', 'CCM Start Date', 'Care Plan Review Date', 'Status'];
    const rows = patientsToExport.map(p => [
      p.last_name || '',
      p.first_name || '',
      p.date_of_birth || '',
      p.phone_number || '',
      p.ccm_start_date || '',
      p.care_plan_review_date || '',
      needsContinueOnboarding(p) ? 'Needs Onboarding' : 'Active'
    ]);

    const escape = (v: string) =>
      v.includes(',') || v.includes('"') || v.includes('\n')
        ? `"${v.replace(/"/g, '""')}"`
        : v;

    const csv = [headers.join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);

    const userName = user?.name || 'user';
    const dateStr = new Date().toISOString().slice(0, 10);
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `patient-list-${userName}-${dateStr}-${timestamp}.csv`;

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`CSV exported (${rows.length} patients)`);
  }, [user, currentOrgId, debouncedSearchQuery, sortField, sortDirection, gridFilters]);

  if (loading) {
    return (
      <Box className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent mx-auto mb-4"></div>
        <Text size="3" className="text-slate-600">Loading patients...</Text>
      </Box>
    );
  }

  return (
    <Box className="space-y-6">
      <Flex justify="between" align="center" gap="4" className="mb-6">
        {/* PHA-898: filters are anchored to the LEFT, immediately after the
            heading, so the Status select sits next to "Patient Management" and
            keeps that spot regardless of which sibling filters are showing.
            (They previously lived in the right-hand action group, which is
            right-aligned and variable-width, so Status slid horizontally every
            time a sub-filter appeared.) Order: Status → Stage → Consent → Tier.
            Stage and Consent render only for Needs Onboarding; Tier is always
            right-most and renders only where a tier exists — see
            showApcmTierFilter. */}
        <Flex gap="2" align="center" wrap="wrap">
          <Heading size="6" className="text-slate-800 mr-2">
            Patient Management
          </Heading>
          {/* PHA-1168: a custom view's own filters decide who is in it. */}
          {!activeView && (<>
          <Select.Root size="2" value={statusFilter} onValueChange={(val) => { setStatusFilter(val as 'all' | 'needsOnboarding' | 'onboarded'); if (val !== 'needsOnboarding') { setOnboardingSubFilter('all'); setOnboardingStageFilter('all'); } }}>
            <Select.Trigger placeholder="Status" />
            <Select.Content>
              <Select.Item value="all">All Statuses</Select.Item>
              <Select.Item value="needsOnboarding">Needs Onboarding</Select.Item>
              <Select.Item value="onboarded">Onboarded</Select.Item>
            </Select.Content>
          </Select.Root>
          {statusFilter === 'needsOnboarding' && (
            <>
              <Select.Root size="2" value={onboardingStageFilter} onValueChange={(val) => setOnboardingStageFilter(val as 'all' | 'chartReview' | 'carePlanReview')}>
                <Select.Trigger placeholder="Onboarding Stage" />
                <Select.Content>
                  <Select.Item value="all">All Stages</Select.Item>
                  <Select.Item value="chartReview">Chart Review</Select.Item>
                  <Select.Item value="carePlanReview">Care Plan Review</Select.Item>
                </Select.Content>
              </Select.Root>
              <Select.Root size="2" value={onboardingSubFilter} onValueChange={(val) => setOnboardingSubFilter(val as 'all' | 'notCollected' | 'denied')}>
                <Select.Trigger placeholder="Consent" />
                <Select.Content>
                  <Select.Item value="all">All Consent Statuses</Select.Item>
                  <Select.Item value="notCollected">Not Collected</Select.Item>
                  <Select.Item value="denied">Consent Denied</Select.Item>
                </Select.Content>
              </Select.Root>
            </>
          )}
          {showApcmTierFilter && (
            <Select.Root size="2" value={apcmTierFilter} onValueChange={(val) => setApcmTierFilter(val as 'all' | 'tier1' | 'tier2')}>
              <Select.Trigger placeholder="APCM Tier" />
              <Select.Content>
                <Select.Item value="all">All Tiers</Select.Item>
                <Select.Item value="tier1">Tier 1</Select.Item>
                <Select.Item value="tier2">Tier 2</Select.Item>
              </Select.Content>
            </Select.Root>
          )}
          </>)}
        </Flex>
        <Flex gap="2" align="center">
          {customViews.enabled && (customViews.views.length > 0 || customViews.canDesign) && (
            <ViewSelector
              views={customViews.views}
              activeViewId={activeViewId}
              onChange={setViewChoice}
              onManage={customViews.canDesign ? () => navigate('/clinic-settings/views') : undefined}
            />
          )}
          <Button
            variant="outline"
            onClick={() => loadPatients(undefined, { background: true })}
            className="flex items-center gap-2"
            disabled={isRefreshing}
          >
            <ReloadIcon
              width="16"
              height="16"
              className={isRefreshing ? 'animate-spin' : ''}
            />
            {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button
            variant="outline"
            onClick={activeView
              ? async () => {
                  // PHA-1168: the selected view's columns, labels and order.
                  setExporting(true);
                  try { await customViewRef.current?.exportCsv(); }
                  catch (err: any) { toast.error(`Export failed: ${err?.message || 'unknown error'}`); }
                  finally { setExporting(false); }
                }
              : handleExportCSV}
            disabled={exporting}
            className="flex items-center gap-2"
          >
            <DownloadIcon width="16" height="16" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        </Flex>
      </Flex>
      {error && (
        // PHA-1159: a transient reconnect is not an "API Connection Issue" the user has
        // to act on, and it is certainly not mock data — say what is actually happening.
        <Callout.Root color={error === 'Reconnecting…' ? 'blue' : 'orange'} className="mb-4">
          <Callout.Icon>
            <ExclamationTriangleIcon />
          </Callout.Icon>
          <Callout.Text>
            <Text weight="bold">
              {error === 'Reconnecting…' ? 'Reconnecting:' : 'API Connection Issue:'}
            </Text>{' '}
            {error === 'Reconnecting…' ? 'restoring your session' : error}
            <br />
            <Text size="2" className="text-slate-600">
              {error === 'Reconnecting…'
                ? 'Temporary connection problem — retrying automatically, no action needed.'
                : error.includes('Authentication required')
                  ? '💡 Please click the login button below to authenticate with Auth0.'
                  : 'Showing mock data for demonstration. Check browser console for details.'
              }
            </Text>
            {showLoginButton && (
              <Box className="mt-3">
                <Button 
                  variant="solid" 
                  color="blue"
                  onClick={() => {
                    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5802';
                    window.location.href = `${baseUrl}/login`;
                  }}
                >
                  Login with Auth0
                </Button>
              </Box>
            )}
          </Callout.Text>
        </Callout.Root>
      )}

      {/* PHA-1187: patients whose number Twilio rejected — survey texts are paused. */}
      <SmsUndeliverableBanner
        organizationId={currentOrgId}
        refreshKey={smsBannerRefreshKey}
        onOpenPatient={(p) =>
          openOrFocus({
            patientId: p.patient_id,
            firstName: p.first_name ?? undefined,
            lastName: p.last_name ?? undefined,
            mrn: p.patient_id,
          })
        }
      />

      {/* Search and Filters */}
      <Flex gap="3" align="center" wrap="wrap" className="mb-4">
        <TextField.Root size="2" style={{ maxWidth: '400px', flex: 1 }}>
          <TextField.Slot>
            <MagnifyingGlassIcon height="16" width="16" />
          </TextField.Slot>
          <TextField.Input
            placeholder="Search by name, MRN, or date of birth..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </TextField.Root>
        <Text as="label" size="2" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', userSelect: 'none' }}>
          <Checkbox
            size="2"
            checked={appointmentTodayFilter}
            onCheckedChange={(checked) => setAppointmentTodayFilter(checked === true)}
          />
          Appointment Today
        </Text>
        {/* PHA-1083: reads the backend's total instead of measuring the array,
            which is what lets it say "of 137" while holding only 50 rows. */}
        {/* PHA-1168: a custom view shows its own count above its table. */}
        {!activeView && (
          <Badge size="1" variant="soft" color="gray">
            {totalCount === 0
              ? '0 patients'
              : `Showing ${rangeStart}–${rangeEnd} of ${totalCount} patients`}
          </Badge>
        )}
        {isRefreshing && (
          <Text size="1" color="gray">Updating…</Text>
        )}
      </Flex>

      {activeView ? (
        <CustomPatientListView
          ref={customViewRef}
          orgId={currentOrgId}
          viewId={activeView.id}
          viewName={activeView.name}
          search={debouncedSearchQuery}
          filters={viewGridFilters}
          pageSize={pageSize}
          onOpenPatient={(row) =>
            openOrFocus({
              patientId: row.patient_id,
              firstName: typeof row.first_name === 'string' ? row.first_name : undefined,
              lastName: typeof row.last_name === 'string' ? row.last_name : undefined,
              mrn: row.patient_id,
            })
          }
          onUnavailable={(reason) => {
            toast(reason === 'stale'
              ? `"${activeView.name}" needs to be re-saved by a clinic admin. Showing Phanova Standard.`
              : `"${activeView.name}" is no longer available. Showing Phanova Standard.`);
            setViewChoice(STANDARD);
            customViews.refresh();
          }}
        />
      ) : patients.length === 0 ? (
        // PHA-1083: with the filters applied server-side, an empty result is far
        // more often "nothing matches" than "this clinic has no patients", and
        // telling a clinician to add their first patient when they have simply
        // typed a name that isn't there reads as data loss. Offer the way out.
        <Callout.Root color="blue">
          <Callout.Icon>
            <PersonIcon />
          </Callout.Icon>
          <Callout.Text>
            {hasActiveNarrowing ? (
              <Flex align="center" gap="3" wrap="wrap">
                <Text>No patients match the current search or filters.</Text>
                <Button size="1" variant="soft" onClick={handleClearFilters}>
                  Clear search and filters
                </Button>
              </Flex>
            ) : (
              'No patients found. Use the "Add Patient" button to add your first patient.'
            )}
          </Callout.Text>
        </Callout.Root>
      ) : (
        <Card ref={tableCardRef} className="shadow-sm patient-list-table">
          <Table.Root size="1" variant="surface">
            <Table.Header>
              <Table.Row className="bg-slate-50">
                <Table.ColumnHeaderCell style={{ width: '100px', verticalAlign: 'middle', position: 'sticky', left: 0, zIndex: 2, backgroundColor: 'rgb(248, 250, 252)' }}>MRN</Table.ColumnHeaderCell>
                <SortableHeader field="firstName" label="First" style={{ width: '90px', position: 'sticky', left: '100px', zIndex: 2, backgroundColor: 'rgb(248, 250, 252)' }} />
                <SortableHeader field="lastName" label="Last" style={{ width: '110px', position: 'sticky', left: '190px', zIndex: 2, backgroundColor: 'rgb(248, 250, 252)' }} />
                <Table.ColumnHeaderCell style={{ width: '85px', whiteSpace: 'nowrap', verticalAlign: 'middle', position: 'sticky', left: '300px', zIndex: 2, backgroundColor: 'rgb(248, 250, 252)' }}>DOB</Table.ColumnHeaderCell>
                {showClinicColumn && (
                  <Table.ColumnHeaderCell style={{ width: '80px', verticalAlign: 'middle' }}>Clinic</Table.ColumnHeaderCell>
                )}
                <SortableHeader field="timeThisMonth" label="Time (min)" style={{ width: '100px', whiteSpace: 'nowrap', textAlign: 'center' }} />
                <SortableHeader field="latestAnswer" label="Answer" style={{ width: '85px', whiteSpace: 'nowrap' }} />
                <SortableHeader field="lastSent" label="Last Sent" style={{ width: '85px', whiteSpace: 'nowrap' }} />

                <SortableHeader field="bloodPressure" label="BP" style={{ width: '80px' }} />
                <SortableHeader field="bpDate" label="BP Date" style={{ width: '100px', whiteSpace: 'nowrap' }} />
                <SortableHeader field="hospitalization" label="Hosp. Date" style={{ width: '100px', whiteSpace: 'nowrap' }} />
                {showApcmColumns && <SortableHeader field="apcmTier" label="APCM" style={{ width: '60px', whiteSpace: 'nowrap' }} />}
                <SortableHeader
                  field="rpmDataDays"
                  label="RPM"
                  style={{ width: '60px', whiteSpace: 'nowrap', textAlign: 'center' }}
                  tooltip={
                    <Flex direction="column" gap="1">
                      <Text size="1">N/A = no device connected</Text>
                      <Text size="1">0 = no readings yet this cycle</Text>
                      <Text size="1">Min required = 2 dates per cycle</Text>
                      <Text size="1">Hover on each patient to see cycle dates</Text>
                    </Flex>
                  }
                />
                <SortableHeader field="startDate" label="Start Date" style={{ width: '100px', whiteSpace: 'nowrap' }} />
                {/* PHA-1080: the ticket names this column "Care Plan Review Date", and ODA
                    asked for exactly those words over "Due date" on this same field in the
                    printed plan. Kept verbatim, and kept identical to the CSV header.

                    The 150px this shipped with clipped the label to "Care Plan Review Dat"
                    (Zack, 2026-08-31). Two changes, because a width alone would only be a
                    better guess at the same bug:

                    - 200px, sized against the neighbour that does fit ("Next Task/Goal", 14
                      characters at 130px) plus room for the sort-arrow Flex every
                      SortableHeader appends.
                    - whiteSpace: nowrap DROPPED on this one header. That property is what
                      turned "too narrow" into a silent mid-character clip: it forbids the
                      wrap, and the cell's overflow hides the remainder. Allowing the wrap
                      makes the header truncation-proof at any width — if 200px is ever short
                      (a longer label, a different font, a zoomed browser), it breaks to two
                      lines instead of losing text. The other date headers keep nowrap; their
                      labels are less than half this length. */}
                <SortableHeader field="reviewDate" label="Care Plan Review Date" style={{ width: '200px', minWidth: '200px' }} tooltip={<Text size="1">When the next care plan is due. Blank until the patient is onboarded.</Text>} />
                <SortableHeader field="nextDueDate" label="Next Task/Goal" style={{ width: '130px', whiteSpace: 'nowrap' }} />
                <Table.ColumnHeaderCell style={{ width: '160px', verticalAlign: 'middle' }}>Actions</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {sortedPatients.map((patient, index) => (
                <Table.Row key={patient.patient_id} className="hover:bg-slate-50">
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'white' }}>
                    <Flex align="center" gap="1" style={{ overflow: 'hidden', minWidth: 0 }}>
                      <Text
                        size="1"
                        weight="medium"
                        onClick={() => handleViewPatient(patient.patient_id)}
                        style={{
                          cursor: 'pointer',
                          color: 'var(--accent-11)',
                        }}
                        className="hover:underline"
                      >
                        {patient.patient_id}
                      </Text>
                    </Flex>
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'sticky', left: '100px', zIndex: 1, backgroundColor: 'white' }}>
                    <Tooltip content={patient.first_name} delayDuration={200}>
                      <Text size="2" weight="medium" className="text-slate-800">
                        {patient.first_name}
                      </Text>
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'sticky', left: '190px', zIndex: 1, backgroundColor: 'white' }}>
                    <Tooltip content={patient.last_name} delayDuration={200}>
                      <Text size="2" weight="medium" className="text-slate-800">
                        {patient.last_name}
                      </Text>
                    </Tooltip>
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', position: 'sticky', left: '300px', zIndex: 1, backgroundColor: 'white' }}>
                    <Text size="1" className="text-slate-600">
                      {(() => {
                        // Parse date components directly to avoid timezone issues
                        const [year, month, day] = patient.date_of_birth.split('-').map(Number);
                        const date = new Date(year, month - 1, day);
                        return format(date, 'MMM dd, yy');
                      })()}
                    </Text>
                  </Table.Cell>
                  {showClinicColumn && (
                    <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Text size="1" className="text-slate-600">
                        {patient.organization_name || 'N/A'}
                      </Text>
                    </Table.Cell>
                  )}
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    <Badge
                      size="1"
                      color={(patient.timeSpentThisMonth || 0) >= 20 ? "green" : (patient.timeSpentThisMonth || 0) > 0 ? "blue" : "gray"}
                      variant="soft"
                    >
                      {patient.timeSpentThisMonth?.toFixed(0) || '0'}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Text size="1" className="text-slate-600">
                      {patient.last_successful_data_collection
                        ? format(new Date(patient.last_successful_data_collection), 'MMM dd, yy')
                        : 'N/A'}
                    </Text>
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Text size="1" className="text-slate-600">
                      {patient.last_attempt_to_collect_data
                        ? format(new Date(patient.last_attempt_to_collect_data), 'MMM dd, yy')
                        : 'N/A'}
                    </Text>
                  </Table.Cell>

                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const bp = latestBloodPressureFor(patient);
                      if (!bp?.systolic && !bp?.diastolic) {
                        return <Text size="1" className="text-slate-400">N/A</Text>;
                      }
                      const category = classifyBloodPressure(bp.systolic, bp.diastolic);
                      const color = getBPColor(category);
                      return (
                        <Badge size="1" color={color} variant="soft">
                          {bp.systolic || '?'}/{bp.diastolic || '?'}
                        </Badge>
                      );
                    })()}
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {(() => {
                      const bp = latestBloodPressureFor(patient);
                      if (!bp?.date) {
                        return <Text size="1" className="text-slate-400">N/A</Text>;
                      }
                      return (
                        <Text size="1" className="text-slate-600">
                          {format(new Date(bp.date), 'MMM dd, yy')}
                        </Text>
                      );
                    })()}
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Text size="1" className="text-slate-600">
                      {patient.last_hospitalization
                        ? format(new Date(patient.last_hospitalization + 'T00:00:00'), 'MMM dd, yy')
                        : ''}
                    </Text>
                  </Table.Cell>
                  {showApcmColumns && (
                    <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                      {patient.apcm_tier && (
                        <Badge
                          size="1"
                          color={patient.apcm_tier === 1 ? 'blue' : 'purple'}
                          variant="soft"
                        >
                          T{patient.apcm_tier}
                        </Badge>
                      )}
                    </Table.Cell>
                  )}
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
                    {/* PHA-1226: a linked device with no readings yet shows 0, not N/A. */}
                    {patient.has_rpm_data || patient.has_rpm_device ? (
                      <Tooltip
                        content={
                          patient.rpm_cycle_start && patient.rpm_cycle_end
                            ? `Cycle: ${format(new Date(patient.rpm_cycle_start + 'T00:00:00'), 'MMM d')} – ${format(new Date(patient.rpm_cycle_end + 'T00:00:00'), 'MMM d')}`
                            : 'No readings yet'
                        }
                      >
                        <Text size="1">{patient.rpm_data_days ?? 0}</Text>
                      </Tooltip>
                    ) : (
                      <Text size="1" color="gray">N/A</Text>
                    )}
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Text size="1" className="text-slate-600">
                      {patient.ccm_start_date
                        ? format(parseISO(patient.ccm_start_date), 'MMM dd, yy')
                        : ''}
                    </Text>
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Text size="1" className="text-slate-600">
                      {/* PHA-1080/PHA-953: parseISO, never new Date(). Both of these are
                          DATE columns, so they arrive date-only and new Date(str) would
                          parse them as UTC midnight -- a day early in every negative-offset
                          timezone. tsc does not catch it; new Date(string) typechecks. */}
                      {patient.care_plan_review_date
                        ? format(parseISO(patient.care_plan_review_date), 'MMM dd, yy')
                        : ''}
                    </Text>
                  </Table.Cell>
                  <Table.Cell style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <Flex gap="1" align="start">
                      {patient.nextDueDate && patient.nextDueType && (
                        <Tooltip
                          content={`Next ${patient.nextDueType === 'task' ? 'Task' : 'Goal'}: ${patient.nextDueName || 'N/A'}`}
                          delayDuration={200}
                        >
                          <Badge
                            size="1"
                            color="blue"
                            variant="soft"
                          >
                            {format(new Date(patient.nextDueDate), 'MMM dd, yy')}
                          </Badge>
                        </Tooltip>
                      )}
                    </Flex>
                  </Table.Cell>
                  <Table.Cell>
                    <Flex gap="1" align="start">
                      <Button
                        size="1"
                        variant="soft"
                        color="red"
                        onClick={() => handleRemovePatient(
                          patient.patient_id,
                          `${patient.first_name} ${patient.last_name}`
                        )}
                        disabled={deletingPatients.has(patient.patient_id)}
                        className="flex items-center gap-1"
                      >
                        {deletingPatients.has(patient.patient_id) ? (
                          <div className="animate-spin rounded-full h-3 w-3 border border-red-600 border-t-transparent" />
                        ) : (
                          <TrashIcon width="12" height="12" />
                        )}
                        Remove
                      </Button>
                    </Flex>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>

          {/* PHA-1083: pagination controls. Every button changes `page`, which
              refetches — no client-side slicing, so what is shown is always what
              the backend selected under the current search, filters and sort. */}
          <Flex
            align="center"
            justify="between"
            gap="3"
            wrap="wrap"
            className="patient-list-pagination"
          >
            <Text size="1" color="gray">
              {totalCount === 0
                ? 'No patients match the current filters'
                : `Page ${currentPage} of ${totalPages}`}
            </Text>

            <Flex align="center" gap="1" wrap="wrap">
              <Button
                size="1"
                variant="soft"
                disabled={currentPage <= 1}
                onClick={() => setPage(currentPage - 1)}
              >
                ‹ Prev
              </Button>

              {pageNumbers[0] > 1 && (
                <>
                  <Button size="1" variant="soft" onClick={() => setPage(1)}>1</Button>
                  {pageNumbers[0] > 2 && <Text size="1" color="gray">…</Text>}
                </>
              )}

              {pageNumbers.map(number => (
                <Button
                  key={number}
                  size="1"
                  variant={number === currentPage ? 'solid' : 'soft'}
                  onClick={() => setPage(number)}
                >
                  {number}
                </Button>
              ))}

              {pageNumbers[pageNumbers.length - 1] < totalPages && (
                <>
                  {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                    <Text size="1" color="gray">…</Text>
                  )}
                  <Button size="1" variant="soft" onClick={() => setPage(totalPages)}>
                    {totalPages}
                  </Button>
                </>
              )}

              <Button
                size="1"
                variant="soft"
                disabled={currentPage >= totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                Next ›
              </Button>
            </Flex>

            <Flex align="center" gap="2">
              <Text size="1" color="gray">Per page</Text>
              {/* Same shape as the status/tier Selects above. position="popper"
                  because this one sits at the very bottom of a tall scrolled
                  table: Radix's default item-aligned positioning anchors the
                  popup on the selected item over the trigger, and has rendered
                  as an empty dropdown elsewhere in this app near a viewport
                  edge. It always has a value, so it is not the valueless case
                  that bit AdminClinicsPage — popper is just the safe default. */}
              <Select.Root
                size="2"
                value={String(pageSize)}
                onValueChange={value => setPageSize(Number(value))}
              >
                <Select.Trigger placeholder="Per page" />
                <Select.Content position="popper">
                  <Select.Item value="25">25</Select.Item>
                  <Select.Item value="50">50</Select.Item>
                  <Select.Item value="100">100</Select.Item>
                  <Select.Item value="200">200</Select.Item>
                </Select.Content>
              </Select.Root>
            </Flex>
          </Flex>
        </Card>
      )}

      {/* Schedule Scanner Dialog */}
      <ScheduleScanDialog open={scanDialogOpen} onOpenChange={setScanDialogOpen} />

      {/* Calendar Import Dialog */}
      <CalendarImportDialog
        open={calendarDialogOpen}
        onOpenChange={setCalendarDialogOpen}
        onImportComplete={(importId, matchedPatientIds) => {
          setFilteredPatientIds(matchedPatientIds);
          setCalendarFilterEnabled(true);
        }}
      />
    </Box>
  );
};

export default PatientList;
